import { useEffect, useState } from 'react';
import { ClipboardList, Loader2, Send, AlertCircle } from 'lucide-react';
import {
  Commission,
  parseCommissionBrief,
  listCommissions,
  getCommission,
  approveCommission,
  retireCommission,
  updateCommission,
} from '../lib/api/commissions.js';
import { getPersonasForTarget, PersonaOption } from '../lib/api/index.js';
import { listEgressTargets } from '../lib/api/egress.js';
import type { EgressTargetSummary } from '../lib/api/types.js';

/** The assignment desk (EDITORIAL_COMMISSIONS_PLAN §7.5): free-text intent
 *  box → editable proposal card (suggest-then-confirm) + open-commission
 *  counts. Nothing generates until the operator approves. */
export function CommissionsCard({ entityId }: { entityId?: string }) {
  const [brief, setBrief] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [proposal, setProposal] = useState<Commission | null>(null);
  const [open, setOpen] = useState<Commission[]>([]);
  const [targets, setTargets] = useState<EgressTargetSummary[]>([]);
  const [personasByTarget, setPersonasByTarget] = useState<
    Record<string, PersonaOption[]>
  >({});

  const refreshOpen = () =>
    listCommissions(entityId ? { entity_id: entityId } : undefined)
      .then(rows =>
        setOpen(
          rows.filter(r =>
            ['proposed', 'approved', 'in_progress'].includes(r.status)
          )
        )
      )
      .catch(() => {});

  useEffect(() => {
    refreshOpen();
    listEgressTargets().then(r => setTargets(r.items)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const loadPersonas = async (targetId: string) => {
    if (personasByTarget[targetId]) return;
    try {
      const res = await getPersonasForTarget(targetId);
      setPersonasByTarget(prev => ({ ...prev, [targetId]: res.personas }));
    } catch {
      /* selector degrades to free display of current slug */
    }
  };

  const submit = async () => {
    if (brief.trim().length < 8 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const card = await parseCommissionBrief(brief.trim(), entityId);
      setProposal(card);
      if (card.target_id) void loadPersonas(card.target_id);
      setBrief('');
      void refreshOpen();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(
        detail?.problems?.join(' · ') ||
          detail?.message ||
          (typeof detail === 'string' ? detail : 'Brief could not be parsed')
      );
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    if (!proposal || busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await approveCommission(proposal.commission_id);
      setProposal(updated);
      void refreshOpen();
    } catch (e: any) {
      setError(
        e?.response?.data?.detail?.problems?.join(' · ') || 'Approve failed'
      );
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    if (!proposal || busy) return;
    setBusy(true);
    try {
      await retireCommission(proposal.commission_id);
      setProposal(null);
      void refreshOpen();
    } finally {
      setBusy(false);
    }
  };

  const changePersona = async (slug: string) => {
    if (!proposal) return;
    const updated = await updateCommission(proposal.commission_id, {
      persona_slug: slug,
    });
    setProposal(updated);
  };

  const unresolved: string[] =
    (typeof proposal?.proposal === 'object' &&
      (proposal?.proposal as any)?.unresolved) ||
    [];

  return (
    <div className="bg-surface rounded-xl border border-grey-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-grey-400" />
          <h3 className="text-sm font-semibold text-grey-900">
            Commissions — what do you want written?
          </h3>
        </div>
        {open.length > 0 && (
          <span className="text-xs text-grey-500">
            {open.filter(c => c.status === 'proposed').length} proposed ·{' '}
            {open.filter(c => c.status !== 'proposed').length} in flight
          </span>
        )}
      </div>

      <div className="flex gap-2">
        <textarea
          value={brief}
          onChange={e => setBrief(e.target.value)}
          rows={2}
          placeholder='e.g. "Story arc for Memphasys as a feature for Orecast — three parts, last one on the next filing."'
          className="flex-1 text-sm border border-grey-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-primary-400"
        />
        <button
          onClick={submit}
          disabled={busy || brief.trim().length < 8}
          className="self-end inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white disabled:opacity-40 hover:bg-primary-700"
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          Propose
        </button>
      </div>

      {error && (
        <p className="mt-2 text-xs text-danger-600 flex items-start gap-1">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {error}
        </p>
      )}

      {proposal && (
        <div className="mt-3 border border-grey-200 rounded-lg p-3 bg-grey-50">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-grey-900">
              {proposal.content_class.toUpperCase()} ·{' '}
              {targets.find(t => t.target_id === proposal.target_id)
                ?.target_name || proposal.target_name || 'target'}
            </p>
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                proposal.status === 'proposed'
                  ? 'bg-warning-100 text-warning-700'
                  : 'bg-success-100 text-success-700'
              }`}
            >
              {proposal.status}
            </span>
          </div>

          <div className="mt-2 flex items-center gap-2 text-xs text-grey-600">
            <span>Writer:</span>
            <select
              value={proposal.persona_slug}
              onChange={e => void changePersona(e.target.value)}
              disabled={proposal.status !== 'proposed'}
              className="text-xs border border-grey-200 rounded px-1.5 py-0.5 bg-white"
            >
              <option value={proposal.persona_slug}>
                {personasByTarget[proposal.target_id]?.find(
                  p => p.slug === proposal.persona_slug
                )?.display_name || proposal.persona_slug}
              </option>
              {(personasByTarget[proposal.target_id] || [])
                .filter(p => p.slug !== proposal.persona_slug)
                .map(p => (
                  <option key={p.slug} value={p.slug}>
                    {p.display_name}
                  </option>
                ))}
            </select>
          </div>

          <ul className="mt-2 space-y-1.5">
            {(proposal.parts || []).map(part => (
              <li key={part.part_id} className="text-xs text-grey-700">
                <span className="font-semibold">Part {part.part_number}:</span>{' '}
                {part.working_title}
                <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded bg-grey-200 text-grey-600">
                  {part.trigger_type === 'date'
                    ? `on ${part.trigger_date}`
                    : part.trigger_type.replace('_', ' ')}
                </span>
                <span className="ml-1.5 text-grey-400">
                  {part.effective_status || part.status}
                </span>
                {['approved', 'in_progress'].includes(proposal.status) &&
                  part.status === 'pending' && (
                    <>
                      <button
                        className="ml-2 text-primary-600 hover:underline"
                        onClick={async () => {
                          const { triggerCommissionPart } = await import(
                            '../lib/api/commissions.js'
                          );
                          await triggerCommissionPart(
                            proposal.commission_id,
                            part.part_id
                          );
                          setProposal(await getCommission(proposal.commission_id));
                        }}
                      >
                        generate now
                      </button>
                      <button
                        className="ml-2 text-grey-400 hover:underline"
                        onClick={async () => {
                          const { skipCommissionPart } = await import(
                            '../lib/api/commissions.js'
                          );
                          await skipCommissionPart(
                            proposal.commission_id,
                            part.part_id
                          );
                          setProposal(await getCommission(proposal.commission_id));
                        }}
                      >
                        skip
                      </button>
                    </>
                  )}
              </li>
            ))}
          </ul>

          {proposal.anchor_policy && (
            <p className="mt-2 text-xs text-grey-500 italic">
              Anchor: {proposal.anchor_policy}
            </p>
          )}

          {unresolved.length > 0 && (
            <div className="mt-2 text-xs text-warning-700 bg-warning-50 border border-warning-200 rounded p-2">
              {unresolved.map((u, i) => (
                <p key={i}>⚠ {u}</p>
              ))}
            </div>
          )}

          <div className="mt-3 flex gap-2">
            {proposal.status === 'proposed' && (
              <button
                onClick={approve}
                disabled={busy}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-success-600 text-white hover:bg-success-700 disabled:opacity-40"
              >
                Approve commission
              </button>
            )}
            <button
              onClick={discard}
              disabled={busy}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-grey-300 text-grey-600 hover:bg-grey-100 disabled:opacity-40"
            >
              {proposal.status === 'proposed' ? 'Discard' : 'Retire'}
            </button>
            <button
              onClick={() => setProposal(null)}
              className="ml-auto px-3 py-1.5 text-xs text-grey-500 hover:text-grey-700"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {open.length > 0 && !proposal && (
        <ul className="mt-3 space-y-1">
          {open.slice(0, 4).map(c => (
            <li
              key={c.commission_id}
              className="text-xs text-grey-600 flex items-center justify-between"
            >
              <button
                className="truncate text-left hover:text-primary-600"
                onClick={async () => {
                  const full = await getCommission(c.commission_id);
                  setProposal(full);
                  if (full.target_id) void loadPersonas(full.target_id);
                }}
              >
                {c.entity_identifier ? `${c.entity_identifier} · ` : ''}
                {c.brief_text}
              </button>
              <span className="ml-2 shrink-0 text-grey-400">
                {c.status} · {c.pending_part_count}/{c.part_count} pending
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default CommissionsCard;
